import { useState, useEffect, useRef } from "react";
import {
  MessageCircle,
  AudioLines,
  Image,
  Newspaper,
  Maximize,
  Minimize,
  SquarePen,
  Settings2,
  Settings,
  Star,
  Wand2,
  HelpCircle,
  LogOut,
  PanelLeftClose,
  MoreHorizontal,
  Share2,
  Trash2,
  Download,
  Pin,
  PinOff,
  Search,
  ChevronRight,
  ChevronDown,
  User,
  ArrowUpRight,
  Moon,
  Sun,
  ChevronLeft,
  Mail,
  Sparkle,
  Globe,
} from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompass } from '@fortawesome/free-regular-svg-icons';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { StorageManager } from "@/utils/storageManager";
import canvasIconImg from "/src/assets/canvas-page.png";
import { useTheme } from "../contexts/ThemeContext";
import { useUser } from "../hooks/use-user";
import { useTempChat } from "../contexts/TempChatContext";
import oreogImg from "/src/assets/image.png";
import LookAroundIcon from "../../packages/svgs/LookAroundIcon";
import { useToast } from "@/hooks/use-toast";

import { cn } from "@/lib/utils";
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scrollLock';
import { ShareDialog } from "./ShareDialog";
import { EditIcon, LibraryIcon } from "../../packages/svgs";
import { ReportBugModal } from "@/components/ReportBugModal";
import { Loader2 } from "lucide-react";


import { createRoot } from 'react-dom/client';
import { ChatPdfRenderer } from './ChatPdfRenderer';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onAuth: () => void;
  user: any;
  onNewChat: () => void;
  isDarkMode: boolean;
  onSelectChat?: (chatId: string) => void;
  planTier?: string;
  onUpgradeNeeded?: (message: string) => void;
}

interface ChatSession {
  id: string;
  title: string;
  created_at: string;
  last_message?: string;
  isPinned?: boolean;
  isShared?: boolean;
}

const getDisplayEmail = (email?: string | null) => {
  if (!email) return "";
  return email.split("@")[0] || email;
};

const byPrefixAndName = {
  far: {
    compass: faCompass,
  },
} as const;

const DiscoverIcon = ({ className }: { className?: string }) => (
  <FontAwesomeIcon icon={byPrefixAndName.far['compass']} className={className} />
);

// TypewriterTitle component for animating new chat titles
const TypewriterTitle = ({
  title,
  className,
  speed = 25,
  createdAt
}: {
  title: string;
  className?: string;
  speed?: number;
  createdAt?: string;
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const hasAnimatedRef = useRef<boolean>(false);
  const previousTitleRef = useRef<string>(title);

  useEffect(() => {
    // Determine if this is a new chat (created within last 10 seconds)
    const isRecentlyCreated = createdAt
      ? (Date.now() - new Date(createdAt).getTime()) < 10000
      : false;

    // Check if title just changed from placeholder
    const wasPlaceholder = previousTitleRef.current === 'Generating…' ||
      previousTitleRef.current === 'New Chat' ||
      previousTitleRef.current === '';

    const titleChanged = title !== previousTitleRef.current;

    // Animate if: title changed from placeholder OR is a recently created chat that hasn't animated yet
    const shouldAnimate = (titleChanged && wasPlaceholder) ||
      (isRecentlyCreated && !hasAnimatedRef.current && title !== 'Generating…');

    if (shouldAnimate && title && title !== 'Generating…') {
      hasAnimatedRef.current = true;
      setIsAnimating(true);
      setDisplayedText('');
      let i = 0;
      const interval = setInterval(() => {
        if (i < title.length) {
          setDisplayedText(title.slice(0, i + 1));
          i++;
        } else {
          clearInterval(interval);
          setIsAnimating(false);
        }
      }, speed);
      previousTitleRef.current = title;
      return () => clearInterval(interval);
    } else {
      setDisplayedText(title);
      previousTitleRef.current = title;
    }
  }, [title, speed, createdAt]);

  return (
    <span className={className}>
      {displayedText.split(/\s+/).slice(0, 10).join(' ')}
      {isAnimating && <span className="animate-pulse">|</span>}
    </span>
  );
};

const navigationItems = [
  // {
  //   label: "Discover",
  //   href: "/discover",
  //   icon: DiscoverIcon,
  // },
  {
    label: "OG",
    href: "/og",
    icon: Globe,
    badge: "BETA V3",
  },
  {
    label: "Chitra AI",
    href: "/chitra/home",
    icon: Image,
  },
  // {
  //   label: "Library",
  //   href: "/chitra/library",
  //   icon: LibraryIcon,
  // },
  // {
  //   label: "Canvas",
  //   href: "https://oreogio.netlify.app",
  //   icon: SquarePen,
  // },
  {
    label: "Aawaz",
    href: "/aawaz",
    icon: AudioLines,
  },
  {
    label: "AI News",
    href: "/ai-news",
    icon: Newspaper,
  },
];

export function Sidebar({
  isOpen,
  onToggle,
  onAuth,
  user,
  onNewChat,
  isDarkMode,
  dailyMessageCount = 0,
  onSelectChat,
  planTier = 'free',
  onUpgradeNeeded,
}: SidebarProps & { dailyMessageCount?: number }) {
  const { theme, setTheme } = useTheme();
  const { user: currentUser, logout } = useUser();
  const { isTempMode, setTempMode, getTempMessagesCount, startIncognitoSession } = useTempChat();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [recentChats, setRecentChats] = useState<ChatSession[]>([]);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [activeChatMenu, setActiveChatMenu] = useState<string | null>(null);
  const [downloadMenuChatId, setDownloadMenuChatId] = useState<string | null>(null);
  const [downloadingChat, setDownloadingChat] = useState<{ id: string; format: 'json' | 'txt' | 'pdf' } | null>(null);
  const [isHelpSubmenuOpen, setIsHelpSubmenuOpen] = useState(false);
  const [showReportBugModal, setShowReportBugModal] = useState(false);
  const [confirmDeleteChat, setConfirmDeleteChat] = useState<ChatSession | null>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingChat, setRenamingChat] = useState<ChatSession | null>(null);
  const [newChatName, setNewChatName] = useState("");
  const [pinOverflowConfirm, setPinOverflowConfirm] = useState<{ toPin: ChatSession; bottomPinned: { id: string; title: string } } | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const [isDeletingChat, setIsDeletingChat] = useState(false);
  // Temp tooltip state for custom tooltip rendering outside overflow containers
  const tempBtnRef = useRef<HTMLButtonElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [tempTipPos, setTempTipPos] = useState<{ left: number; top: number } | null>(null);
  const [tempTipVisible, setTempTipVisible] = useState(false);
  // Fullscreen (Focus mode) tooltip state
  const fullscreenBtnRef = useRef<HTMLButtonElement | null>(null);
  const [fullTipPos, setFullTipPos] = useState<{ left: number; top: number } | null>(null);
  const [fullTipVisible, setFullTipVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  // Smooth fullscreen transition overlay state
  const [fsOverlayVisible, setFsOverlayVisible] = useState(false);
  const [fsOverlayOpaque, setFsOverlayOpaque] = useState(false);
  // Simple responsive flag for tooltip logic
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const { toast } = useToast();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedChatForShare, setSelectedChatForShare] = useState<ChatSession | null>(null);

  // Chats collapse state and measurement
  const [chatsCollapsed, setChatsCollapsed] = useState(false);
  const chatsSectionRef = useRef<HTMLDivElement | null>(null);
  const [chatsMaxHeight, setChatsMaxHeight] = useState<number>(0);

  // Measure chats section height dynamically for smooth animation
  useEffect(() => {
    const el = chatsSectionRef.current;
    if (!el) return;

    // Initial measure
    setChatsMaxHeight(el.scrollHeight);

    // Observe size changes
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        setChatsMaxHeight(el.scrollHeight);
      });
      ro.observe(el);
    }

    const onResize = () => setChatsMaxHeight(el.scrollHeight);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, [recentChats, chatSearchQuery, isOpen]);

  const authenticatedUser = currentUser;

  const [profileData, setProfileData] = useState<{ avatar_url?: string; full_name?: string } | null>(null);

  useEffect(() => {
    if (!authenticatedUser) return;

    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('avatar_url, full_name')
        .eq('user_id', authenticatedUser.id)
        .maybeSingle();

      if (!error && data) {
        setProfileData(data);
      }
    };

    fetchProfile();

    // Listen for profile updates
    const channel = supabase
      .channel('profile-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles',
        filter: `user_id=eq.${authenticatedUser.id}`
      }, (payload) => {
        setProfileData(payload.new as any);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [authenticatedUser]);

  useEffect(() => {
    if (!isOpen) return;
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 768) return;

    const body = document.body;
    const previousBodyTouchAction = body.style.touchAction;

    // Lock body scroll centrally
    lockBodyScroll();
    // Also disable touch actions to prevent iOS rubber banding while sidebar is open
    body.style.touchAction = "none";

    return () => {
      // Restore in the correct order to avoid flicker
      body.style.touchAction = previousBodyTouchAction;
      unlockBodyScroll();
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = sidebarRef.current;
      const menuButton = document.querySelector(
        '[aria-label="Toggle sidebar"]'
      );

      if (
        sidebar &&
        !sidebar.contains(event.target as Node) &&
        !menuButton?.contains(event.target as Node) &&
        isOpen
      ) {
        onToggle();
      }

      if (showProfileDropdown && sidebar && !sidebar.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    const handlePopState = () => {
      if (isOpen) {
        onToggle();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isOpen, onToggle, showProfileDropdown]);

  useEffect(() => {
    // Debounced, de-duplicated loader to avoid spamming Supabase
    let destroyed = false;
    let inFlight = false;
    let pending = false;
    let lastFetchAt = 0;
    let loadTimer: number | null = null;
    const DEBOUNCE_MS = 300; // small debounce since triggers are explicit (on send)

    const runLoad = async () => {
      if (destroyed) return;
      if (inFlight) { pending = true; return; }
      inFlight = true;
      try {
        if (authenticatedUser) {
          // For authenticated users, use Supabase sessions
          const { data, error } = await supabase
            .from('chat_sessions')
            .select('id, title, created_at, is_pinned, is_shared')
            .eq('user_id', authenticatedUser.id)
            .order('is_pinned', { ascending: false })
            .order('updated_at', { ascending: false });

          if (error) throw error;

          const recentSessions = (data || []).map(session => ({
            id: session.id,
            title: session.title,
            created_at: session.created_at,
            isPinned: session.is_pinned ?? false,
            isShared: (session as any)?.is_shared ?? false
          }));
          setRecentChats(recentSessions);
        } else {
          // For guests, use local storage
          const storageManager = StorageManager.getInstance();
          const sessions = storageManager.getAllSessions();
          const recentSessions = sessions.map(session => ({
            id: session.id,
            title: session.title,
            created_at: typeof (session.createdAt || session.timestamp) === 'string'
              ? (session.createdAt || session.timestamp) as string
              : new Date(session.createdAt || session.timestamp).toISOString(),
            isPinned: (session as any)?.isPinned ?? false,
            isShared: false
          }));
          setRecentChats(recentSessions);
        }
      } catch (error) {
        console.error("");
      } finally {
        inFlight = false;
        if (pending && !destroyed) {
          pending = false;
          scheduleLoad();
        }
      }
    };

    const scheduleLoad = () => {
      if (destroyed) return;
      const now = Date.now();
      const elapsed = now - lastFetchAt;
      // Debounce bursts of triggers
      if (elapsed < DEBOUNCE_MS) {
        if (loadTimer) window.clearTimeout(loadTimer);
        loadTimer = window.setTimeout(() => {
          lastFetchAt = Date.now();
          runLoad();
        }, DEBOUNCE_MS - elapsed);
        return;
      }
      lastFetchAt = now;
      if (loadTimer) {
        window.clearTimeout(loadTimer);
        loadTimer = null;
      }
      runLoad();
    };

    // Initial load
    scheduleLoad();

    // Refresh only when a message is sent (messageCountChanged is dispatched on send)
    const handleMessageCountChanged = () => scheduleLoad();
    window.addEventListener('messageCountChanged', handleMessageCountChanged as any);

    return () => {
      destroyed = true;
      window.removeEventListener('messageCountChanged', handleMessageCountChanged as any);
      if (loadTimer) window.clearTimeout(loadTimer);
    };
  }, [authenticatedUser]);



  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      if (!activeChatMenu && !downloadMenuChatId) return;

      const rawTarget = event.target as Node | null;
      const targetElement = rawTarget instanceof Element ? rawTarget : rawTarget?.parentElement ?? null;

      const isWithinMenu = targetElement?.closest('[data-chat-menu-container]');
      const isTrigger = targetElement?.closest('[data-chat-menu-trigger]');

      if (!isWithinMenu && !isTrigger) {
        setActiveChatMenu(null);
        setDownloadMenuChatId(null);
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveChatMenu(null);
        setDownloadMenuChatId(null);
      }
    };

    document.addEventListener("mousedown", handleGlobalClick);
    document.addEventListener("keydown", handleKeydown);

    return () => {
      document.removeEventListener("mousedown", handleGlobalClick);
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [activeChatMenu, downloadMenuChatId]);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      onToggle();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };


  const handleNewChatClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startNewChat = () => {
      // If currently in incognito mode, disable it before starting a persistent chat
      if (isTempMode) {
        setTempMode(false);
      }
      if (onNewChat) {
        onNewChat();
      }
      if (isOpen) {
        setTimeout(() => onToggle(), 100);
      }
    };

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(startNewChat, 120);
    } else {
      startNewChat();
    }
  };

  const handleNavigation = (callback?: () => void) => {
    if (isOpen) onToggle();
    if (callback) callback();
  };

  const handleCloseSidebar = (
    event?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (isOpen) {
      onToggle();
    }
  };

  const toggleFullscreen = () => {
    // Start smooth overlay fade-in
    setFsOverlayVisible(true);
    requestAnimationFrame(() => setFsOverlayOpaque(true));

    const finish = () => {
      // Fade back out after a short delay to cover browser transition
      setTimeout(() => {
        setFsOverlayOpaque(false);
        setTimeout(() => setFsOverlayVisible(false), 220);
      }, 120);
    };

    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
        .then(finish)
        .catch((err) => {
          console.error("Error attempting to enable fullscreen:", err);
          // Ensure overlay hides if request fails
          setFsOverlayOpaque(false);
          setFsOverlayVisible(false);
        });
    } else {
      document.exitFullscreen()
        .then(finish)
        .catch((err) => {
          console.error("Error attempting to exit fullscreen:", err);
          setFsOverlayOpaque(false);
          setFsOverlayVisible(false);
        });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    handleFullscreenChange();

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Track viewport width for mobile tooltip behavior
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchChatTranscript = async (chatId: string) => {
    try {
      if (authenticatedUser) {
        const { data, error } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('session_id', chatId)
          .order('message_order', { ascending: true });

        if (error) throw error;
        return (data || []).map((message: any) => {
          // Parse complex fields if they are stored as strings in DB but we expect objects
          // Note: Supabase JS client usually handles JSON columns automatically if defined as JSONB

          return {
            role: message.role,
            content: message.content,
            model: message.model,
            metadata: message.metadata,
            is_image: message.is_image,
            attached_media_url: message.attached_media_url,
            attached_media_type: message.attached_media_type,

            // Map snake_case DB columns to camelCase expected by Renderer/APP
            selectedAgent: message.selected_agent,
            agentData: message.agent_data,
            searchResults: message.search_results,
            parallelSources: message.parallel_sources,
            checkResult: message.check_result,
            laraResults: message.lara_results,
            isLARA: message.is_lara,
            weatherData: message.weather_data,

            // Handle attachments list if present
            attachedMedia: message.attached_media,
            attachedMediaList: message.attached_media_list
          };
        });
      }

      const storageManager = StorageManager.getInstance();
      const session = storageManager.getSession(chatId);
      if (!session?.messages?.length) return [];
      return session.messages.map((message: any) => ({
        role: message.type === 'user' ? 'user' : 'assistant',
        content: message.content,
        model: message.model,
        selectedAgent: message.selectedAgent,
        agentData: message.agentData,
        searchResults: message.searchResults,
        parallelSources: message.parallelSources,
        isImage: message.isImage,
        laraResults: message.laraResults,
        isLARA: message.isLARA,
        weatherData: message.weatherData,
        attachedMedia: message.attachedMedia,
        attachedMediaList: message.attachedMediaList,
      }));
    } catch (error) {
      console.error('Failed to fetch chat transcript:', error);
      toast({
        title: "Error",
        description: "Unable to load chat transcript",
        variant: "destructive",
      });
      return [];
    }
  };

  const handleShareChat = async (chat: ChatSession) => {
    // Check if chat has messages
    const transcript = await fetchChatTranscript(chat.id);
    if (!transcript.length) {
      toast({
        title: "No messages",
        description: "This chat has no messages to share yet.",
        variant: "destructive",
      });
      return;
    }

    // Open share dialog
    setSelectedChatForShare(chat);
    setShareDialogOpen(true);
    setActiveChatMenu(null);
    setDownloadMenuChatId(null);
  };

  const handleDeleteChat = async (chat: ChatSession) => {
    try {
      // Only authenticated & verified users can delete server chats
      if (authenticatedUser) {
        // Check email verification status
        // Supabase User typically provides email_confirmed_at when verified
        const isVerified = Boolean((authenticatedUser as any).email_confirmed_at);
        if (!isVerified) {
          toast({
            title: "Verification required",
            description: "Please verify your email to delete chats.",
            variant: "destructive",
          });
          return;
        }

        setIsDeletingChat(true);
        const { error } = await supabase
          .from('chat_sessions')
          .delete()
          .eq('id', chat.id)
          .eq('user_id', authenticatedUser.id);

        if (error) throw error;
      } else {
        // Guest/local sessions: allow local deletion only
        const storageManager = StorageManager.getInstance();
        storageManager.deleteSession(chat.id);
      }

      setRecentChats((prev) => prev.filter((session) => session.id !== chat.id));
      toast({
        title: "Deleted",
        description: "",
      });

      // If currently viewing this chat, navigate to a fresh chat
      const isOnThisChat = location.pathname.startsWith('/c/') && location.pathname.includes(chat.id);
      if (isOnThisChat) {
        if (onNewChat) {
          onNewChat();
        } else {
          navigate('/', { replace: true });
        }
      }
      // Close modal after successful deletion
      setConfirmDeleteChat(null);
    } catch (error) {
      console.error('Failed to delete chat:', error);
      toast({
        title: "Error",
        description: "Unable to delete chat",
        variant: "destructive",
      });
    } finally {
      setIsDeletingChat(false);
      setActiveChatMenu(null);
      setDownloadMenuChatId(null);
    }
  };

  const handleRenameChat = async () => {
    if (!newChatName.trim() || !renamingChat) return;

    try {
      if (authenticatedUser) {
        const { error } = await supabase
          .from('chat_sessions')
          .update({ title: newChatName.trim() })
          .eq('id', renamingChat.id)
          .eq('user_id', authenticatedUser.id);

        if (error) throw error;
      } else {
        const storageManager = StorageManager.getInstance();
        storageManager.updateSession(renamingChat.id, { title: newChatName.trim() });
      }

      // Update local state
      setRecentChats((prev) =>
        prev.map((chat) =>
          chat.id === renamingChat.id ? { ...chat, title: newChatName.trim() } : chat
        )
      );

      setShowRenameModal(false);
      setRenamingChat(null);
      setNewChatName("");
      setActiveChatMenu(null);

    } catch (error) {
      console.error('Failed to rename chat:', error);
      toast({
        title: "Error",
        description: "Failed to rename chat",
        variant: "destructive",
      });
    }
  };

  const togglePinChat = async (chat: ChatSession) => {
    const nextPinned = !chat.isPinned;
    try {
      if (nextPinned) {
        // Enforce max 10 pinned
        if (authenticatedUser) {
          const { data: pinnedList, error: listErr } = await supabase
            .from('chat_sessions')
            .select('id, title, updated_at')
            .eq('user_id', authenticatedUser.id)
            .eq('is_pinned', true)
            .order('updated_at', { ascending: true }); // oldest first
          if (listErr) throw listErr;
          const count = pinnedList?.length || 0;
          if (count >= 10) {
            const bottom = pinnedList![0];
            setPinOverflowConfirm({ toPin: chat, bottomPinned: { id: bottom.id, title: bottom.title } });
            return;
          }
          const { error } = await supabase
            .from('chat_sessions')
            .update({ is_pinned: true })
            .eq('id', chat.id)
            .eq('user_id', authenticatedUser.id);
          if (error) throw error;
        } else {
          const storageManager = StorageManager.getInstance();
          const all = storageManager.getAllSessions();
          const pinned = all.filter((s: any) => !!s.isPinned).sort((a: any, b: any) => new Date(a.updatedAt || a.timestamp).getTime() - new Date(b.updatedAt || b.timestamp).getTime());
          if (pinned.length >= 10) {
            const bottom = pinned[0];
            setPinOverflowConfirm({ toPin: chat, bottomPinned: { id: bottom.id, title: bottom.title } });
            return;
          }
          storageManager.updateSession(chat.id, { isPinned: true } as any);
        }
        setRecentChats((prev) => prev
          .map((session) => session.id === chat.id ? { ...session, isPinned: true } : session)
          .sort((a, b) => (b.isPinned === a.isPinned) ? 0 : b.isPinned ? 1 : -1)
        );
      } else {
        if (authenticatedUser) {
          const { error } = await supabase
            .from('chat_sessions')
            .update({ is_pinned: false })
            .eq('id', chat.id)
            .eq('user_id', authenticatedUser.id);
          if (error) throw error;
        } else {
          const storageManager = StorageManager.getInstance();
          storageManager.updateSession(chat.id, { isPinned: false } as any);
        }
        setRecentChats((prev) => prev.map((s) => s.id === chat.id ? { ...s, isPinned: false } : s));
        toast({ title: "Unpinned", description: "Chat unpinned" });
      }
    } catch (error) {
      console.error('Failed to update pin status:', error);
      toast({
        title: "Error",
        description: "Failed to update pin status",
        variant: "destructive",
      });
    } finally {
      setActiveChatMenu(null);
      setDownloadMenuChatId(null);
    }
  };

  const confirmPinOverflow = async () => {
    if (!pinOverflowConfirm) return;
    const { toPin, bottomPinned } = pinOverflowConfirm;
    try {
      if (authenticatedUser) {
        const { error: unpinErr } = await supabase
          .from('chat_sessions')
          .update({ is_pinned: false })
          .eq('id', bottomPinned.id)
          .eq('user_id', authenticatedUser.id);
        if (unpinErr) throw unpinErr;
        const { error: pinErr } = await supabase
          .from('chat_sessions')
          .update({ is_pinned: true })
          .eq('id', toPin.id)
          .eq('user_id', authenticatedUser.id);
        if (pinErr) throw pinErr;
      } else {
        const storageManager = StorageManager.getInstance();
        storageManager.updateSession(bottomPinned.id, { isPinned: false } as any);
        storageManager.updateSession(toPin.id, { isPinned: true } as any);
      }
      setRecentChats((prev) => prev
        .map((s) => s.id === bottomPinned.id ? { ...s, isPinned: false } : s)
        .map((s) => s.id === toPin.id ? { ...s, isPinned: true } : s)
        .sort((a, b) => (b.isPinned === a.isPinned) ? 0 : b.isPinned ? 1 : -1)
      );
      toast({ title: "Pinned", description: `Unpinned oldest and pinned "${toPin.title}"` });
    } catch (e) {
      console.error('Pin overflow confirm failed:', e);
      toast({ title: "Error", description: "Failed to update pinned chats", variant: "destructive" });
    } finally {
      setPinOverflowConfirm(null);
    }
  };

  const trimmedSearch = chatSearchQuery.trim().toLowerCase();
  const filteredChats = trimmedSearch
    ? recentChats.filter((chat) => chat.title.toLowerCase().includes(trimmedSearch))
    : recentChats;

  const handleDownloadPDF = async (chat: ChatSession) => {
    setDownloadingChat({ id: chat.id, format: 'pdf' });
    try {
      const transcript = await fetchChatTranscript(chat.id);
      if (!transcript.length) {
        toast({
          title: "No messages",
          description: "This chat has no messages to download.",
          variant: "destructive",
        });
        setDownloadingChat(null);
        return;
      }

      const title = chat.title || 'Chat Export';

      // Create hidden iframe for native print (vector PDF)
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow?.document;
      if (!doc) {
        throw new Error("Could not create iframe document");
      }

      // Write basic HTML structure
      doc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${title}</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <script src="https://cdn.tailwindcss.com"></script>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
            <style>
               body { font-family: 'Inter', sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
               @page { margin: 10mm; size: auto; }
               /* Hide scrollbars in print */
               ::-webkit-scrollbar { display: none; }
            </style>
          </head>
          <body>
            <div id="pdf-root"></div>
          </body>
        </html>
      `);
      doc.close();

      // Wait for iframe capability
      await new Promise(resolve => setTimeout(resolve, 500));

      const rootEl = doc.getElementById('pdf-root');
      if (rootEl) {
        const root = createRoot(rootEl);
        root.render(
          <ChatPdfRenderer
            messages={transcript}
            title={title}
            userName={
              profileData?.full_name ||
              (authenticatedUser as any)?.user_metadata?.full_name ||
              (authenticatedUser?.email?.split('@')[0]) ||
              'User'
            }
          />
        );

        // Wait for rendering and resources (images, fonts)
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Print
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }

        // Cleanup after delay
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 10000); // Give enough time for print dialog interaction

        toast({
          title: "Ready to Print",
          description: "",
        });
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF",
        variant: "destructive",
      });
    } finally {
      setDownloadingChat(null);
      setActiveChatMenu(null);
      setDownloadMenuChatId(null);
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[59] md:hidden"
          style={{ height: "100dvh" }}
          onClick={onToggle}
        />
      )}
      <aside
        className={`fixed top-0 left-0 z-[60] h-[100vh] border-r transition-all duration-300 select-none ${isOpen ? "translate-x-0 shadow-xl" : "-translate-x-full"
          } w-[17rem] ${isDarkMode
            ? "bg-[#1c1c1c] border-[#121212]"
            : "bg-white border-gray-200"
          }`}
        ref={sidebarRef}
        style={{ height: "100dvh" }}
      >
        <div
          className={`flex flex-col h-full overflow-hidden ${isDarkMode ? "bg-[#181818]" : "bg-[#f9f9f9]"}`}
        >
          {/* Header with Close Button */}
          <div
            className={`flex items-center justify-between p-3 border-b ${isDarkMode ? "border-[#121212]" : "border-gray-200"
              }`}
          >
            <button
              type="button"
              aria-label="Close sidebar"
              className={`flex items-center text-lg font-semibold group cursor-e-resize ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
              onClick={handleCloseSidebar}
            >
              <div className="w-7 h-7 rounded-lg overflow-hidden mr-3 relative">
                <img
                  src={oreogImg}
                  alt="Logo"
                  className="h-7 object-cover transition-opacity duration-200 group-hover:opacity-0"
                />
                <PanelLeftClose
                  strokeWidth={1}
                  className={`absolute inset-0 w-8 h-8 pb-[6px] pr-[4px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                />
              </div>
              <h2 className="instrument-serif-regular text-2xl">OREOG</h2>
            </button>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Toggle fullscreen"
                ref={fullscreenBtnRef}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleFullscreen();
                }}
                onMouseEnter={() => {
                  if (fullscreenBtnRef.current) {
                    const rect = fullscreenBtnRef.current.getBoundingClientRect();
                    setFullTipPos({ left: rect.left + rect.width / 2, top: rect.bottom + 8 });
                    setFullTipVisible(true);
                  }
                }}
                onMouseLeave={() => setFullTipVisible(false)}
                className={`p-2 rounded-xl transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#2e2e2e]" : "text-gray-600 hover:bg-gray-100"}
                `}
              >
                {isFullscreen ? (
                  <Minimize className="h-4 w-4" />
                ) : (
                  <Maximize className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {fullTipVisible && fullTipPos && (
            <div
              className="fixed z-[99999]  px-2 py-1 text-xs rounded-md bg-black text-white whitespace-nowrap shadow-lg"
              style={{ left: fullTipPos.left, top: fullTipPos.top, transform: 'translate(-70%, 0)' }}
            >
              Focus mode
            </div>
          )}

          <div
            className="flex-1 px-3 scrollbar-none flex flex-col overflow-hidden"
          // style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.5rem)" }}
          >
            {/* New Chat and Temp Mode Buttons */}
            <div className="mb-1 max-w-auto pt-2 flex gap-2 ">
              <Button
                onClick={handleNewChatClick}
                className={`group flex-1 justify-start rounded-2xl border-0 py-[6px] ${isDarkMode
                  ? "bg-transparent hover:bg-[#2e2e2e] text-white"
                  : "bg-transparent hover:bg-gray-100 text-gray-900"
                  }`}
              >
                <SquarePen className="w-4 h-4" />
                <span className="flex-1 text-left">New Chat</span>
                <span
                  className={` bg-transparent ${isDarkMode
                    ? "text-gray-300"
                    : "text-gray-600"
                    } inline-flex items-center hidden md:block gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-150 font-mono`}
                  aria-hidden="true"
                >
                  Ctrl+Shift+O
                </span>
              </Button>

              {/* Temporary Chat Mode Button - Only for logged users */}
              {authenticatedUser && (
                <Button
                  ref={tempBtnRef}
                  onClick={() => {
                    const isPro = planTier?.toLowerCase() === 'pro' || planTier?.toLowerCase() === 'enterprise' || planTier?.toLowerCase() === 'starter';
                    if (!isTempMode && !isPro) {
                      // Free user trying to access incognito - show upgrade
                      if (onUpgradeNeeded) {
                        onUpgradeNeeded("Upgrade to access Incognito mode");
                      }
                      if (isOpen) onToggle();
                      return;
                    }
                    if (!isTempMode) {
                      // Starting incognito mode - clear messages, navigate to incognito, show modal
                      startIncognitoSession();
                      if (location.pathname !== '/incognito') {
                        navigate('/incognito');
                      }
                      if (onNewChat) {
                        onNewChat(); // Start a fresh chat
                      }
                    } else {
                      // Disabling incognito mode
                      setTempMode(false);
                      if (location.pathname === '/incognito') {
                        navigate('/');
                      }
                    }
                    if (isOpen) onToggle();
                  }}
                  onMouseEnter={() => {
                    if (tempBtnRef.current) {
                      const rect = tempBtnRef.current.getBoundingClientRect();
                      setTempTipPos({ left: rect.left + rect.width / 2, top: rect.top - 8 });
                      setTempTipVisible(true);
                    }
                  }}
                  onMouseLeave={() => setTempTipVisible(false)}
                  className={`h-10 w-10 p-0 rounded-2xl transition-all duration-200 relative group ${isTempMode
                    ? isDarkMode
                      ? "bg-[#121212] hover:bg-[#303030] text-white border-2 border-dashed border-blue-500"
                      : "bg-white text-black hover:bg-gray-300 border-2 border-dashed border-blue-500"
                    : isDarkMode
                      ? "bg-transparent hover:bg-[#303030] text-white"
                      : "bg-transparent hover:bg-gray-100 text-gray-900"
                    }`}
                >
                  <LookAroundIcon size={isTempMode ? 24 : 20} />
                  {isTempMode && getTempMessagesCount() > 0 && (
                    <span className={`absolute -top-1 -right-1 text-xs px-1.5 py-0.5 rounded-full ${isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-200 text-gray-800"
                      }`}>
                      {getTempMessagesCount()}
                    </span>
                  )}
                </Button>
              )}
              {tempTipVisible && tempTipPos && (
                <div
                  className="fixed z-[99999] px-2 py-1 text-xs rounded-md bg-black text-white whitespace-nowrap shadow-lg"
                  style={{ left: tempTipPos.left, top: tempTipPos.top, transform: 'translate(-70%, -100%)' }}
                >
                  {isTempMode ? 'Disable Incognito' : 'Incognito Mode'}
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="space-y-[2px] mb-4">
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isExternal = /^https?:\/\//.test(item.href);
                const isActive = (!isExternal && (location.pathname === item.href ||
                  (item.href === "/ai-news" && location.pathname === "/sudoku")));

                const activeClasses = isDarkMode
                  ? "bg-white/10 text-slate-100 shadow-[0_14px_32px_-22px_rgba(15,15,20,0.8)]"
                  : "bg-primary/10 text-primary-700 shadow-[0_16px_36px_-24px_rgba(59,130,246,0.45)]";

                const inactiveClasses = isDarkMode
                  ? "text-gray-300 hover:bg-[#2e2e2e] hover:text-white"
                  : "text-slate-800 hover:bg-slate-100 hover:text-slate-900";

                const commonInner = (
                  <>
                    {item.label === "Canvas" ? (
                      <>
                        <img
                          src={canvasIconImg}
                          alt="Canvas"
                          className="w-6 h-6 mr-0.5 rounded-sm"
                        />
                        {item.label}
                        <ArrowUpRight
                          className={cn(
                            "ml-auto w-4 h-4 transition-colors",
                            isActive
                              ? isDarkMode
                                ? "text-slate-100"
                                : "text-primary"
                              : isDarkMode
                                ? "text-gray-400"
                                : "text-slate-500"
                          )}
                        />
                      </>
                    ) : (
                      <>
                        <IconComponent
                          className={cn(
                            "w-4 h-4 mr-3 transition-colors",
                            isActive
                              ? isDarkMode
                                ? "text-slate-100"
                                : "text-primary"
                              : isDarkMode
                                ? "text-gray-400"
                                : "text-slate-800"
                          )}
                        />

                        <span>{item.label}</span>

                        {item.badge && (
                          <span
                            className={cn(
                              "ml-3 px-1.5 py-0.5 text-[10px] font-semibold rounded-lg",
                              isDarkMode
                                ? "bg-blue-500/20 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                            )}
                          >
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </>
                );

                return isExternal ? (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleNavigation()}
                    className={cn(
                      "group flex items-center rounded-xl px-3 py-[7px] text-sm font-medium transition-all duration-200",
                      isActive ? activeClasses : inactiveClasses
                    )}
                  >
                    {commonInner}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => handleNavigation()}
                    className={cn(
                      "group flex items-center rounded-xl px-3 py-[7px] text-sm font-medium transition-all duration-200",
                      isActive ? activeClasses : inactiveClasses
                    )}
                  >
                    {commonInner}
                  </Link>
                );
              })}
            </nav>

            {/* Recent Chats Section */}
            {recentChats.length > 0 && (
              <div className="flex-1 min-h-0 flex flex-col">
                <button
                  type="button"
                  aria-label={chatsCollapsed ? 'Expand chats' : 'Collapse chats'}
                  aria-expanded={!chatsCollapsed}
                  aria-controls="sidebar-chats-section"
                  onClick={() => setChatsCollapsed((v) => !v)}
                  className={`flex items-center justify-between w-full text-left mb-2 px-2 py-1 rounded-md select-none ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}
                >
                  <h3
                    className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                  >
                    CHATS
                  </h3>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${chatsCollapsed ? '-rotate-90' : 'rotate-0'
                      }`}
                  />
                </button>

                <div
                  className={`overflow-auto transition-[max-height,opacity] duration-300 ease-in-out flex flex-col ${chatsCollapsed ? 'opacity-0' : 'opacity-100'
                    }`}
                  style={{ maxHeight: chatsCollapsed ? 0 : Math.max(chatsMaxHeight, 0) }}
                  id="sidebar-chats-section"
                >
                  <div ref={chatsSectionRef}>
                    <div className="mb-3">
                      <div
                        className={`flex items-center gap-2 rounded-2xl border-0.5 px-3 py-2 border ${isDarkMode
                          ? "border-[#2e2e2e] bg-transparent hover:bg-[#303030] active:bg-[#303030] focus-within:bg-[#303030] text-gray-200"
                          : "border-gray-200 bg-white text-gray-700"
                          }`}
                      >
                        <Search className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        <input
                          type="text"
                          value={chatSearchQuery}
                          onChange={(event) => setChatSearchQuery(event.target.value)}
                          placeholder="Search chats..."
                          className={`flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400 ${isDarkMode ? "text-gray-100" : "text-gray-900"
                            }`}
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-h-[16rem] space-y-[1px] pb-4 pr-1 overflow-y-auto">
                      {filteredChats.map((chat) => {
                        const isActiveChatId = location.pathname === `/c/${chat.id}`;
                        return (
                          <div
                            key={chat.id}
                            className={`group relative flex flex-col px-3 py-[5px] rounded-2xl cursor-pointer transition-colors ${isActiveChatId
                              ? "dark:bg-[#505050] bg-gray-200 text-white shadow-sm dark:shadow-md"
                              : isDarkMode
                                ? "hover:bg-[#2e2e2e]"
                                : "hover:bg-gray-100"
                              }`}
                            onClick={() => {
                              // Auto-disable incognito when switching to any other chat
                              if (isTempMode) {
                                setTempMode(false);
                              }
                              if (onSelectChat) {
                                onSelectChat(chat.id);
                              } else {
                                navigate(`/c/${chat.id}`);
                              }
                              if (isOpen) onToggle();
                            }}
                          >
                            <div className="flex items-center">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className={`text-sm font-medium truncate max-w-[30ch] ${isActiveChatId
                                  ? "dark:text-white text-black"
                                  : isDarkMode
                                    ? "text-gray-300"
                                    : "text-gray-700"
                                  }`}>
                                  <TypewriterTitle
                                    title={chat.title}
                                    className=""
                                    createdAt={chat.created_at}
                                  />
                                </span>
                              </div>
                              <button
                                type="button"
                                className={`ml-2 p-1.5 rounded-full transition-colors ${isActiveChatId
                                  ? "text-black dark:text-white hover:bg-white/10"
                                  : isDarkMode
                                    ? "text-gray-400 hover:text-white hover:bg-[#2a2a2a]"
                                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"
                                  } ${isActiveChatId || chat.isPinned
                                    ? "opacity-100"
                                    : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                  }`}
                                data-chat-menu-trigger
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setDownloadMenuChatId(null);
                                  setActiveChatMenu((prev) => prev === chat.id ? null : chat.id);
                                }}
                              >
                                {chat.isPinned ? (
                                  <div className="relative w-4 h-4 flex items-center justify-center">
                                    <Pin
                                      className={`absolute inset-0 w-4 h-4 transition-opacity group-hover:opacity-0 rotate-[45deg] ${isDarkMode ? "fill-[#afafaf] text-[#afafaf]" : "fill-[#8f8f8f] text-[#8f8f8f]"
                                        } opacity-50`}
                                    />
                                    <MoreHorizontal className="absolute inset-0 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                ) : (
                                  <MoreHorizontal className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                            {activeChatMenu === chat.id && (
                              <div
                                data-chat-menu-container
                                className={`absolute right-2 top-full mt-2 w-48 rounded-2xl border shadow-xl z-[70] ${isDarkMode
                                  ? "bg-[#1c1c1c] border-[#2e2e2e] text-gray-200"
                                  : "bg-white border-gray-200 text-gray-800"
                                  }`}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className={`w-full flex items-center rounded-t-2xl justify-between px-3 py-2 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                    }`}
                                  onClick={() => {
                                    setRenamingChat(chat);
                                    setNewChatName(chat.title);
                                    setShowRenameModal(true);
                                    setActiveChatMenu(null);
                                  }}
                                >
                                  <span className="flex items-center gap-2">
                                    <EditIcon className="w-4 h-4" />
                                    Rename
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                    }`}
                                  onClick={() => handleShareChat(chat)}
                                >
                                  <span className={`flex items-center gap-2 ${chat.isShared ? 'text-blue-500' : ''}`}>
                                    <Share2 className="w-4 h-4" />
                                    {chat.isShared ? 'Shared' : 'Share'}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                    }`}
                                  onClick={() => togglePinChat(chat)}
                                >
                                  <span className="flex items-center gap-2">
                                    {chat.isPinned ? (
                                      <PinOff className="w-4 h-4" />
                                    ) : (
                                      <Pin className="w-4 h-4" />
                                    )}
                                    {chat.isPinned ? "Unpin" : "Pin"}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                    }`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDownloadPDF(chat);
                                    setActiveChatMenu(null);
                                  }}
                                  disabled={!!downloadingChat && downloadingChat.id === chat.id}
                                >
                                  <span className="flex items-center gap-2">
                                    <Download className="w-4 h-4" />
                                    Download as PDF
                                  </span>
                                  {downloadingChat && downloadingChat.id === chat.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : null}
                                </button>
                                <button
                                  type="button"
                                  className={`w-full flex items-center rounded-b-2xl justify-between px-3 py-2 text-sm text-red-500 transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                    }`}
                                  onClick={() => setConfirmDeleteChat(chat)}
                                >
                                  <span className="flex items-center gap-2">
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {filteredChats.length === 0 && (
                        <div
                          className={`px-3 py-4 text-sm text-center rounded-2xl ${isDarkMode ? "text-gray-500 bg-[#111111]" : "text-gray-500 bg-gray-100"
                            }`}
                        >
                          No chats found.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Message Counter for Non-Authenticated Users */}
          {!authenticatedUser && (
            <div
              className={`my-4 w-full max-w-[calc(100%-1.5rem)] mx-auto flex-shrink-0 rounded-xl overflow-hidden 
              ${isDarkMode
                  ? "[background:linear-gradient(45deg,#1a1a2e,theme(colors.slate.900)_50%,#1a1a2e)_padding-box,conic-gradient(from_var(--border-angle),theme(colors.slate.700)_80%,_theme(colors.indigo.600)_86%,_theme(colors.indigo.400)_90%,_theme(colors.indigo.600)_94%,_theme(colors.slate.700))_border-box]"
                  : "[background:linear-gradient(45deg,#ffffff,theme(colors.slate.50)_50%,#ffffff)_padding-box,conic-gradient(from_var(--border-angle),theme(colors.slate.200)_80%,theme(colors.sky.400)_86%,theme(colors.sky.300)_90%,theme(colors.sky.400)_94%,theme(colors.slate.200))_border-box]"
                }
              border border-transparent animate-border`}
            >
              <div
                className={`p-3 ${isDarkMode ? "bg-[#1e1e1e]" : "bg-gray-200"}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p
                      className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"
                        }`}
                    >
                      {dailyMessageCount < 5
                        ? `${5 - dailyMessageCount} of 5 messages remaining`
                        : "Message limit reached"}
                    </p>
                    <p
                      className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                    >
                      {dailyMessageCount < 5
                        ? "Sign in for unlimited messages"
                        : "Sign in to continue chatting"}
                    </p>
                  </div>
                </div>
                {dailyMessageCount < 5 && (
                  <div className="w-full bg-gray-400 rounded-full h-1.5 mt-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-indigo-500 to-blue-400 h-full rounded-full transition-all duration-500"
                      style={{ width: `${(dailyMessageCount / 5) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bottom Section */}
          <div
            className={`p-3 py-1 w-full flex-shrink-0 border-t-2 ${isDarkMode ? "border-[#303030]" : "border-gray-200"
              }`}
          >
            {/* User Section */}
            {authenticatedUser ? (
              <div className="space-y-0 flex flex-row justify-between relative">
                <div
                  className={`flex items-center gap-3 w-full p-2 py-1 rounded-2xl cursor-pointer transition-colors ${isDarkMode
                    ? "hover:bg-[#3a3a3a]"
                    : "hover:bg-gray-200"
                    }`}
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profileData?.avatar_url || authenticatedUser.user_metadata?.avatar_url} />
                    <AvatarFallback className="bg-transparent border-[2px] flex items-center justify-center border-gray-400 text-white text-lg">
                      {(profileData?.full_name || authenticatedUser.email)?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col min-w-0 flex-1">
                    <span
                      className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {(() => {
                        const fullName = profileData?.full_name ||
                          authenticatedUser?.user_metadata?.full_name ||
                          authenticatedUser?.user_metadata?.name ||
                          authenticatedUser?.email?.split("@")[0] ||
                          "Not provided";

                        // Extract first name only
                        const name = fullName === "Not provided" ? fullName : (fullName.trim().split(/\s+/)[0] || fullName);

                        return name.length > 18 ? `${name.substring(0, 18)}...` : name;
                      })()}
                    </span>
                    <span
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                    >
                      {authenticatedUser.user_metadata?.provider || ""}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label="Toggle theme"
                    className={`p-2 rounded-xl transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#3a3a3a]" : "text-gray-700 hover:bg-gray-100"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = isDarkMode ? 'light' : 'dark';
                      setTheme(next as any);
                    }}
                  >
                    {isDarkMode ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* Custom Profile Dropdown */}
                {showProfileDropdown && (
                  <div
                    className={`absolute bottom-12 left-0 right-0 mb-2 rounded-2xl border-2 shadow-xl ${isDarkMode ? 'bg-[#353535] border-[#2e2e2e]' : 'bg-white border-gray-200'
                      }`}
                  >
                    <div className="p-2 space-y-1">
                      {isHelpSubmenuOpen ? (
                        <>
                          {/* Back Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsHelpSubmenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 p-2 pl-4 rounded-xl text-left transition-colors font-medium mb-1 ${isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Back
                          </button>

                          {/* Terms */}
                          <button
                            onClick={() => {
                              navigate('/terms');
                              setShowProfileDropdown(false);
                              if (isOpen) onToggle();
                            }}
                            className={`w-full flex items-center gap-2 p-2 pl-4 rounded-xl text-left transition-colors ${isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            Terms and Conditions
                          </button>

                          {/* Privacy */}
                          <button
                            onClick={() => {
                              navigate('/privacy');
                              setShowProfileDropdown(false);
                              if (isOpen) onToggle();
                            }}
                            className={`w-full flex items-center gap-2 p-2 pl-4 rounded-xl text-left transition-colors ${isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            Privacy Policy
                          </button>

                          {/* Logout */}
                          <button
                            onClick={handleLogout}
                            className={`w-full flex items-center gap-2 p-2 pl-4 rounded-xl text-left transition-colors ${isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            <LogOut className="h-4 w-4" />
                            Log out
                          </button>


                          {/* Divider */}
                          <div className={`my-1 border-t ${isDarkMode ? 'border-[#3c3c3c]' : 'border-gray-200'}`} />

                          {/* Report Bug */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowReportBugModal(true);
                              setShowProfileDropdown(false);
                              if (isOpen) onToggle();
                            }}
                            className={`w-full flex items-center gap-2 p-2 pl-4 rounded-xl text-left transition-colors ${isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            Report a Bug
                          </button>

                        </>
                      ) : (
                        <>
                          {/* Header with email */}
                          <div className={`flex items-center gap-2 px-2 py-2 border-b ${isDarkMode ? 'text-gray-300 border-[#3c3c3c]' : 'text-gray-700'}`}>
                            <Mail className="h-4 w-4" />
                            <div className="text-sm truncate">{authenticatedUser.email}</div>
                          </div>

                          {/* Upgrade plan */}
                          <button
                            onClick={() => {
                              navigate('/upgrade');
                              setShowProfileDropdown(false);
                              if (isOpen) onToggle();
                            }}
                            className={`w-full flex items-center gap-2 p-2 pl-4 rounded-xl text-left transition-colors ${isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            <Sparkle className="h-4 w-4" />
                            Upgrade Plan
                          </button>

                          {/* Personalization */}
                          <button
                            onClick={() => {
                              navigate('/settings/profile');
                              setShowProfileDropdown(false);
                              if (isOpen) onToggle();
                            }}
                            className={`w-full flex items-center gap-2 p-2 pl-4 rounded-xl text-left transition-colors ${location.pathname === '/settings/user/account'
                              ? 'bg-[#303030] text-white'
                              : isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            <User className="h-4 w-4" />
                            Profile
                          </button>

                          {/* Settings */}
                          <button
                            onClick={() => {
                              navigate('/settings/account');
                              setShowProfileDropdown(false);
                              if (isOpen) onToggle();
                            }}
                            className={`w-full flex items-center gap-2 p-2 pl-4 rounded-xl text-left transition-colors ${location.pathname.startsWith('/settings')
                              ? 'bg-[#303030] text-white'
                              : isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            <Settings className={`h-4 w-4 ${location.pathname.startsWith('/settings') ? 'text-white' : ''}`} />
                            Settings
                          </button>

                          {/* Divider */}
                          <div className={`my-1 border-t ${isDarkMode ? 'border-[#3c3c3c]' : 'border-gray-200'}`} />

                          {/* Help */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsHelpSubmenuOpen(true);
                            }}
                            className={`w-full flex items-center justify-between p-2 pl-4 rounded-xl text-left transition-colors ${isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            <span className="flex items-center gap-2">
                              <HelpCircle className="h-4 w-4" />
                              Help
                            </span>
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => {
                        navigate('/auth');
                        onToggle();
                      }}
                      className={`flex-1 justify-center py-2 flex items-center gap-2 rounded-full shadow-sm transition-colors ${isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                        }`}
                    >
                      Sign in with Email
                    </Button>
                    <Link
                      to="/settings/user/account"
                      onClick={() => handleNavigation()}
                      className={`rounded-full w-10 h-10 flex-shrink-0 border flex items-center justify-center transition-colors
                        ${location.pathname.startsWith('/settings')
                          ? "bg-blue-600 border-blue-600 hover:bg-blue-700"
                          : isDarkMode
                            ? "border-gray-600 bg-[#1c1c1c] hover:bg-gray-800"
                            : "border-gray-300 bg-white hover:bg-gray-100"
                        }`}
                      title="Settings"
                    >
                      <Settings className={`h-4 w-4 ${location.pathname.startsWith('/settings')
                        ? "text-white"
                        : isDarkMode
                          ? "text-white"
                          : "text-black"
                        }`} />
                    </Link>
                  </div>
                  <p
                    className={`text-xs text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                  >
                    Sign in to save your chats and access more features.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Smooth fullscreen transition overlay */}
      {fsOverlayVisible && (
        <div
          className={`fixed inset-0 z-[99998] pointer-events-none bg-black transition-opacity duration-200 ${fsOverlayOpaque ? 'opacity-40' : 'opacity-0'}`}
          style={{ height: '100dvh' }}
        />
      )}

      {/* Pin Overflow Confirmation Modal */}
      {pinOverflowConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPinOverflowConfirm(null)} />
          <div className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${isDarkMode ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}>
            <h3 className="text-lg font-semibold mb-2">Pin limit reached</h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
              You can pin up to 10 chats. Pinning "{pinOverflowConfirm.toPin.title}" will unpin the oldest pinned chat: "{pinOverflowConfirm.bottomPinned.title}".
            </p>
            <div className="flex justify-end gap-2">
              <button
                className={`px-3 py-2 rounded-full ${isDarkMode ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300' : 'bg-transparent hover:bg-gray-100 text-gray-700'}`}
                onClick={() => setPinOverflowConfirm(null)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-full ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                onClick={confirmPinOverflow}
              >
                Unpin & Pin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Chat Modal */}
      {showRenameModal && renamingChat && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRenameModal(false)} />
          <div className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${isDarkMode ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}>
            <h3 className="text-lg font-semibold mb-2">Rename Chat</h3>
            <input
              type="text"
              value={newChatName}
              onChange={(e) => setNewChatName(e.target.value)}
              placeholder="Enter new chat name"
              className={`w-full px-3 py-2 rounded-lg border mb-4 ${isDarkMode
                ? 'bg-[#2a2a2a] border-[#3a3a3a] text-white placeholder-gray-500'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameChat();
                }
              }}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                className={`px-3 py-2 rounded-full ${isDarkMode ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300' : 'bg-transparent hover:bg-gray-100 text-gray-700'}`}
                onClick={() => setShowRenameModal(false)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-full ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                onClick={handleRenameChat}
                disabled={!newChatName.trim()}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteChat && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => !isDeletingChat && setConfirmDeleteChat(null)} />
          <div className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${isDarkMode ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}>
            <h3 className="text-lg font-semibold mb-2">Delete Chat</h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
              Are you sure you want to delete "{confirmDeleteChat.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className={`${isDarkMode ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300' : 'bg-transparent hover:bg-gray-100 text-gray-700'} px-3 py-2 rounded-full disabled:opacity-60`}
                onClick={() => !isDeletingChat && setConfirmDeleteChat(null)}
                disabled={isDeletingChat}
              >
                Cancel
              </button>
              <button
                className={`${isDarkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} px-4 py-2 rounded-full disabled:opacity-70`}
                onClick={() => { if (confirmDeleteChat && !isDeletingChat) handleDeleteChat(confirmDeleteChat); }}
                disabled={isDeletingChat}
                aria-busy={isDeletingChat}
              >
                {isDeletingChat ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      {selectedChatForShare && (
        <ShareDialog
          isOpen={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setSelectedChatForShare(null);
          }}
          chatId={selectedChatForShare.id}
          chatTitle={selectedChatForShare.title}
          isDarkMode={isDarkMode}
        />
      )}
      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => !isLoggingOut && setShowLogoutConfirm(false)} />
          <div className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${isDarkMode ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}>
            <h3 className="text-lg font-semibold mb-2">Log out</h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
              Are you sure you want to log out?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className={`px-3 py-2 rounded-full ${isDarkMode ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300' : 'bg-transparent hover:bg-gray-100 text-gray-700'}`}
                onClick={() => !isLoggingOut && setShowLogoutConfirm(false)}
                disabled={isLoggingOut}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-full flex items-center gap-2 ${isDarkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} ${isLoggingOut ? 'opacity-70 cursor-not-allowed' : ''}`}
                onClick={confirmLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoggingOut ? 'Logging out...' : 'Log out'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReportBugModal
        isOpen={showReportBugModal}
        onClose={() => setShowReportBugModal(false)}
        userEmail={authenticatedUser?.email}
      />
    </>
  );
}
